<template>
  <v-row>
    <!-- Hours field -->
    <v-col
      cols="12"
      class="align-baseline d-flex"
      :class="$vuetify.breakpoint.xs ? 'flex-column' : 'flex-row'"
      ref="hours"
    >
      <label class="title-label"
        >Hours*
        <tip-icon>
          Enter the number of hours the volunteer is expected to work.<br />
          This number is used by your volunteer check-in feature.
          <br />For multi-date needs, Enter the average number of hours expected
          each day.
        </tip-icon>
      </label>
      <v-text-field
        v-model="need.hours"
        label="Example: 4 or 3.5"
        :class="$vuetify.breakpoint.xs ? 'w-100' : 'ml-5'"
        solo
        flat
        outlined
        dense
        :min="1"
        type="number"
        :error-messages="hoursMessage"
        @input="onChange"
      />
    </v-col>

    <!-- Hours Description field -->
    <v-col
      cols="12"
      class="align-baseline d-flex"
      :class="$vuetify.breakpoint.xs ? 'flex-column' : 'flex-row'"
      ref="hours_description"
    >
      <label class="title-label"
        >Hours Description*
        <tip-icon>
          Let the volunteer know when the need is scheduled to start and end.
        </tip-icon>
      </label>
      <v-text-field
        v-model="need.hours_description"
        label="Example: 9am - 5pm"
        :class="$vuetify.breakpoint.xs ? 'w-100' : 'ml-5'"
        solo
        flat
        outlined
        dense
        :error-messages="hoursDescriptionMessage"
      />
    </v-col>

    <!-- Capacity field -->
    <v-col
      cols="12"
      class="align-baseline d-flex"
      :class="$vuetify.breakpoint.xs ? 'flex-column' : 'flex-row'"
      ref="capacity"
    >
      <label class="title-label"
        >Capacity*
        <tip-icon>
          Enter the number of volunteer spots available.
        </tip-icon>
      </label>
      <v-text-field
        v-model="need.capacity"
        label="Example: 30"
        :class="$vuetify.breakpoint.xs ? 'w-100' : 'ml-5'"
        solo
        flat
        outlined
        dense
        :error-messages="capacityMessage"
        :min="1"
        type="number"
        @input="onCapacityInput"
      />
    </v-col>

    <!-- Start Date field -->
    <v-col
      cols="12"
      class="align-baseline d-flex"
      :class="$vuetify.breakpoint.xs ? 'flex-column' : 'flex-row'"
      ref="start_date"
    >
      <label class="title-label"
        >Start Date*
        <tip-icon>
          Select the date when the need is scheduled to start.
        </tip-icon>
      </label>
      <v-menu
        v-model="startDateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formattedStartDate"
            label="Select Start Date"
            readonly
            solo
            flat
            outlined
            dense
            v-bind="attrs"
            v-on="on"
            :error-messages="startDateErrorMessage"
            :class="$vuetify.breakpoint.xs ? 'w-100' : 'ml-5'"
          />
        </template>
        <v-date-picker
          v-model="need.start_date"
          :min="new Date().toISOString().substr(0, 10)"
          @input="onStartDateChange"
        ></v-date-picker>
      </v-menu>
    </v-col>

    <!-- End Date field -->
    <v-col
      cols="12"
      class="align-baseline d-flex"
      :class="$vuetify.breakpoint.xs ? 'flex-column' : 'flex-row'"
      ref="end_date"
    >
      <label class="title-label"
        >End Date*
        <tip-icon>
          Select the date when the need is scheduled to end.
        </tip-icon>
      </label>
      <v-menu
        v-model="endDateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formattedEndDate"
            label="Select End Date"
            :error-messages="endDateErrorMessage"
            readonly
            solo
            flat
            outlined
            dense
            v-bind="attrs"
            v-on="on"
            :class="$vuetify.breakpoint.xs ? 'w-100' : 'ml-5'"
          />
        </template>
        <v-date-picker
          v-model="need.end_date"
          :min="endDateMinimum"
          @input="endDateMenu = false"
        ></v-date-picker>
      </v-menu>
    </v-col>
  </v-row>
</template>

<script>
import TipIcon from "@/components/common/TipIcon.vue";

export default {
  components: { TipIcon },
  props: {
    need: Object,
    hoursMessage: String,
    onChange: Function,
    capacityMessage: String,
    startDateErrorMessage: String,
    hoursDescriptionMessage: String,
    endDateErrorMessage: String,
    registrationClosedDateMessage: String
  },
  data() {
    return {
      date_picker: false,
      endDate: null,
      date_picker_closed_date: false,
      closedDate: null,
      startDateMenu: false,
      endDateMenu: false
    };
  },
  computed: {
    formattedStartDate() {
      if (!this.need.start_date) return "";
      const [year, month, day] = this.need.start_date.split("-");
      return `${month}/${day}/${year}`; // Format as MM/DD/YYYY
    },
    formattedEndDate() {
      if (!this.need.end_date) return "";
      const [year, month, day] = this.need.end_date.split("-");
      return `${month}/${day}/${year}`; // Format as MM/DD/YYYY
    },
    endDateMinimum() {
      // If start date is selected, use that as minimum for end date
      // Otherwise, use today as minimum
      if (this.need.start_date) {
        return this.need.start_date;
      }
      return new Date().toISOString().substr(0, 10);
    }
  },
  methods: {
    onCapacityInput(value) {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue === "" || Number(numericValue) < 1) {
        this.need.capacity = 1;
      } else {
        this.need.capacity = Number(numericValue);
      }
    },
    onStartDateChange() {
      this.startDateMenu = false;
      
      // If end date is selected and it's before the new start date, clear it
      if (this.need.end_date && this.need.start_date && this.need.end_date < this.need.start_date) {
        this.need.end_date = null;
      }
    },
    changeDateFormat(dateStr) {
      return `${dateStr.substring(5, 7)}/${dateStr.substring(
        8,
        10
      )}/${dateStr.substring(0, 4)}`;
    },
    onSaveEndDate(dateStr) {
      this.need.end_date = this.changeDateFormat(dateStr);
      this.date_picker = false;
    },
    onSaveClosedDate(dateStr) {
      this.need.registration_closed_date = this.changeDateFormat(dateStr);
      this.date_picker_closed_date = false;
    }
  },
  mounted() {
    // Remove auto-set of start_date, let user pick it
  }
};
</script>

<style scoped>
.title-label {
  width: 200px;
}
</style>